import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useNavigate } from 'react-router-dom';
import { useQuickGuideItems } from './useQuickGuideItems';

const INITIAL_PENDING_VISIBLE = 3;

function CompletedItem({ title }: { title: string }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
        gap: { xs: 1, sm: 2 },
        alignItems: 'center',
        px: { xs: 1.5, sm: 2 },
        py: { xs: 0.75, sm: 1 },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        bgcolor: alpha(theme.palette.success.light, 0.08),
        opacity: 0.8,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
        <Box sx={{ color: 'success.main', display: 'flex', flexShrink: 0 }}>
          <CheckCircleIcon fontSize="small" />
        </Box>
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
        >
          {title}
        </Typography>
      </Stack>
      <Chip
        label="Completado"
        color="success"
        variant="outlined"
        size="small"
        sx={{ justifySelf: { xs: 'flex-start', sm: 'end' }, fontWeight: 600 }}
      />
    </Box>
  );
}

export default function QuickGuideDashboardSection() {
  const theme = useTheme();
  const navigate = useNavigate();
  const items = useQuickGuideItems();
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) return null;

  const completedItems = items.filter((item) => item.completed);
  const pendingItems = items.filter((item) => !item.completed);
  const completedCount = completedItems.length;
  const progress = Math.round((completedCount / items.length) * 100);

  const primaryItem = pendingItems[0];
  const secondaryPendingItems = pendingItems.slice(1, INITIAL_PENDING_VISIBLE);
  const hiddenPendingItems = pendingItems.slice(INITIAL_PENDING_VISIBLE);
  const hiddenCount = hiddenPendingItems.length;

  const allDone = pendingItems.length === 0;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 4,
        p: { xs: 2, md: 3 },
        border: '1px solid',
        borderColor: 'primary.100',
        borderRadius: { xs: 3, md: 2 },
        bgcolor: 'primary.50',
        overflow: 'hidden',
      }}
    >
      {/* ── Compact header when all done ── */}
      {allDone && (
        <>
          <Stack
            direction="row"
            spacing={{ xs: 1, sm: 2 }}
            alignItems="center"
            sx={{ mb: 0.5, minWidth: 0, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
          >
            <Chip
              label={`${completedCount}/${items.length}`}
              color="primary"
              size="small"
              sx={{ fontWeight: 800, flexShrink: 0 }}
            />
            <Typography
              variant="subtitle1"
              fontWeight={800}
              sx={{ flex: { xs: '1 1 140px', sm: '0 0 auto' }, minWidth: 0, lineHeight: 1.25 }}
            >
              ¡Todo completado!
            </Typography>
            <LinearProgress
              variant="determinate"
              value={100}
              color="success"
              sx={{
                flex: 1,
                height: 8,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.success.light, 0.3),
                display: { xs: 'none', sm: 'block' },
              }}
            />
            <Button
              size="small"
              color="primary"
              onClick={() => setExpanded((prev) => !prev)}
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                fontWeight: 700,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                ml: { xs: 'auto', sm: 0 },
              }}
            >
              {expanded ? 'Ocultar tareas' : 'Ver tareas'}
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5, lineHeight: 1.45 }}>
            Ya tienes configuradas las acciones principales para aprovechar InversorHub.
          </Typography>
          <Collapse in={expanded} unmountOnExit>
            <Stack spacing={0.75} sx={{ mt: 2 }}>
              {completedItems.map((item) => (
                <CompletedItem key={item.id} title={item.title} />
              ))}
            </Stack>
          </Collapse>
        </>
      )}

      {/* ── Full layout when there are pending items ── */}
      {!allDone && (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2.5, md: 3 }}
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
        >
          {/* Left: progress info */}
          <Box sx={{ flex: { md: '0 0 260px' }, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip
                label={`${completedCount}/${items.length}`}
                color="primary"
                size="small"
                sx={{ fontWeight: 800, flexShrink: 0 }}
              />
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={800}
                sx={{ display: { xs: 'none', sm: 'block' } }}
              >
                Guía rápida
              </Typography>
            </Stack>
            <Typography
              variant="h6"
              fontWeight={800}
              sx={{ mb: 1, fontSize: { xs: '1.15rem', sm: '1.25rem' }, lineHeight: 1.25 }}
            >
              Siguiente paso recomendado
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.45 }}>
              Avanza por las acciones con más impacto para sacar partido a tu cartera y a las
              herramientas.
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              color="primary"
              sx={{
                height: 8,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.light, 0.3),
              }}
            />
          </Box>

          {/* Right: items */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack spacing={1}>
              {/* Primary pending item — full card */}
              {primaryItem && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
                    gap: { xs: 1, sm: 2 },
                    alignItems: 'center',
                    p: { xs: 1.5, sm: 2 },
                    border: '1.5px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1.5,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start" minWidth={0}>
                    <Box sx={{ color: 'primary.main', display: 'flex', pt: 0.25, flexShrink: 0 }}>
                      {primaryItem.icon}
                    </Box>
                    <Box minWidth={0}>
                      <Typography fontWeight={800}>{primaryItem.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {primaryItem.description}
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    variant="contained"
                    onClick={() => navigate(primaryItem.href)}
                    sx={{
                      justifySelf: { xs: 'stretch', sm: 'end' },
                      fontWeight: 700,
                      bgcolor: 'accent.main',
                      color: 'accent.contrastText',
                      boxShadow: 'none',
                      whiteSpace: 'nowrap',
                      '&:hover': { bgcolor: 'accent.dark', boxShadow: 'none' },
                    }}
                  >
                    {primaryItem.ctaLabel}
                  </Button>
                </Box>
              )}

              {/* Secondary pending items — compact */}
              {secondaryPendingItems.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
                    gap: { xs: 1, sm: 2 },
                    alignItems: 'center',
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 1, sm: 1.25 },
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: 'rgba(255,255,255,0.7)',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
                    <Box sx={{ color: 'text.disabled', display: 'flex', flexShrink: 0 }}>
                      <RadioButtonUncheckedIcon fontSize="small" />
                    </Box>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {item.title}
                    </Typography>
                  </Stack>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => navigate(item.href)}
                    sx={{
                      justifySelf: { xs: 'stretch', sm: 'end' },
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.ctaLabel}
                  </Button>
                </Box>
              ))}
            </Stack>

            {/* Hidden pending items — collapsible */}
            {hiddenCount > 0 && (
              <>
                <Collapse in={expanded} unmountOnExit>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {hiddenPendingItems.map((item) => (
                      <Box
                        key={item.id}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
                          gap: { xs: 1, sm: 2 },
                          alignItems: 'center',
                          px: { xs: 1.5, sm: 2 },
                          py: { xs: 1, sm: 1.25 },
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          bgcolor: 'rgba(255,255,255,0.7)',
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
                          <Box sx={{ color: 'text.disabled', display: 'flex', flexShrink: 0 }}>
                            <RadioButtonUncheckedIcon fontSize="small" />
                          </Box>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {item.title}
                          </Typography>
                        </Stack>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => navigate(item.href)}
                          sx={{
                            justifySelf: { xs: 'stretch', sm: 'end' },
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.ctaLabel}
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                </Collapse>
                <Button
                  size="small"
                  color="primary"
                  onClick={() => setExpanded((prev) => !prev)}
                  endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ mt: 1, fontWeight: 600, px: 0.5 }}
                >
                  {expanded ? 'Ver menos' : `Ver ${hiddenCount} pasos más`}
                </Button>
              </>
            )}

            {/* Completed items — always visible at the bottom when pending items exist */}
            {completedItems.length > 0 && (
              <Stack spacing={0.75} sx={{ mt: 2 }}>
                {completedItems.map((item) => (
                  <CompletedItem key={item.id} title={item.title} />
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      )}
    </Paper>
  );
}
